import type { RunState } from '~/types/game'

/**
 * 🔁 Offer decline-cycling (v5.17, per Javi: "when a player declines a
 * relic/skill/creature, it doesn't show those options again until
 * they've all been cycled through — that way there's some hope in
 * target farming a build").
 *
 * Every choice screen that can be walked away from records the road not
 * taken; every roll holds those ids out of the draw until the eligible
 * pool has been offered once around, then the bag reshuffles. Per-run
 * state — a fresh run is a fresh bag.
 *
 * Plain module (no Nuxt auto-imports): the rarity simulation bundles
 * the reward roller outside of Nuxt.
 */
export type OfferCycleKind = 'relic' | 'recruit' | 'skill'

/** The live declined-list for a category, creating the store on demand
 * (pre-v5.17 saves and simulation runs arrive without one). */
export function declinedList(run: RunState, kind: OfferCycleKind): string[] {
  const store = run.declinedOffers ??= { relic: [], recruit: [], skill: [] }
  return store[kind]
}

/**
 * Draws `count` ids through the decline cycle. `draw` is the call
 * site's own roller (rarity weights, synergy nudges) applied to
 * whatever subset the cycle allows.
 *
 * When the unseen remainder can no longer fill the screen, it is dealt
 * in full — every option is guaranteed one showing per cycle — and the
 * bag reshuffles behind it for the leftover slots.
 *
 * `allowReset: false` is for FLOORED draws (boss/elite relic stages
 * narrow the bag to its top rarities): running that slice dry must not
 * wipe the whole category's cycle, so the slice refills from its full
 * self without clearing anything.
 */
export function drawThroughCycle(
  run: RunState,
  kind: OfferCycleKind,
  candidateIds: string[],
  count: number,
  draw: (ids: string[], n: number) => string[],
  allowReset = true,
): string[] {
  const declined = declinedList(run, kind)
  const seen = new Set(declined)
  const unseen = candidateIds.filter(id => !seen.has(id))
  if (unseen.length >= count) return draw(unseen, count)
  const picks = draw(unseen, unseen.length)
  if (allowReset) declined.length = 0
  const dealt = new Set(picks)
  return [...picks, ...draw(candidateIds.filter(id => !dealt.has(id)), count - picks.length)]
}

/** Records the road not taken: every offered id except the chosen one
 * joins the declined list until its cycle completes. */
export function recordDeclinedOffers(
  run: RunState,
  kind: OfferCycleKind,
  offeredIds: readonly string[],
  chosenId?: string | null,
): void {
  const declined = declinedList(run, kind)
  for (const id of offeredIds) {
    if (id !== chosenId && !declined.includes(id)) declined.push(id)
  }
}
